<template>
	<div id="Search">
		<div class="wrapper" ref="scroll-container">
			<search-item-title v-if="mode != 'list'" :item="info" :placeholder="placeholder" @search="search" :mode="mode" />
			<brand-tabs v-if="mode == 'brand'" :brandId="$route.query.id" active="chemical" autoRedirect />
			<!-- 제목 + 검색창 + 필터를 하나의 패널로 (화학물질 목록은 크림색 히어로 패널) -->
			<div class="search-panel" :class="{ hero: mode == 'list' }">
				<!-- 화학물질 목록 상단 검색 (헤더의 통합검색을 대체) -->
				<section class="chemical-search" v-if="mode == 'list'">
					<span class="banner-label">LAB CHEMICALS</span>
					<div class="banner-head">
						<h2>화학물질</h2>
						<p>CAS No, 시약명, 제품번호로 바로 찾아보세요.</p>
					</div>
					<header-search-box placeholder="CAS No, 시약명, 제품번호로 검색" />
				</section>
				<loading v-if="isLoading" :keyword="getLoadingKeyword" />
				<filter-box
					v-show="!isLoading && showFilter"
					:keyword="keyword"
					:list="filterList"
					:selected="selectedCategory()"
					:listLength="catalogList.count"
					:mode="mode"
					@setSelectedFilter="setSelectedFilter"
				/>
			</div>
			<div class="result-summary" v-show="!isLoading && list.length > 0">
				<span>
					전체 <b>{{ formatCount(totalCount) }}</b
					>건
				</span>
				<span v-if="isOverLimit" class="limit-note">상위 {{ formatCount(MAX_RESULT) }}건까지 이동할 수 있어요. 필터로 범위를 좁혀 보세요.</span>
			</div>
			<product-list
				v-show="!isLoading && list.length > 0"
				:list="list"
				:tabList="sortTabList"
				variant="card"
				@sortProduct="sortProduct"
				@searchSuccess="searchSuccess"
			/>
			<!-- 숫자 페이저 (엔진은 앞쪽 MAX_RESULT건까지만 이동 가능) -->
			<common-pagenation
				v-show="!isLoading && list.length > 0 && pagerTotal > 0"
				:key="pagerKey"
				class="search-pager"
				:total="pagerTotal"
				:isPerPage="false"
				:defaultLength="length"
				@page="onPage"
			/>
			<no-product-list v-if="isNoData" title="검색결과" />
		</div>
	</div>
</template>

<script>
import ProductList from '@/components/list/ProductList.vue'
import SearchItemTitle from '@/views/search/components/SearchItemTitle.vue'
import FilterBox from '@/components/search/FilterBox.vue'
import BrandTabs from '@/components/search/BrandTabs.vue'
import HeaderSearchBox from '@/components/HeaderSearchBox.vue'
import Loading from '@/components/Loading.vue'
import NoProductList from '@/components/list/NoProductList.vue'
import CommonPagenation from '@/components/pagenation/CommonPagenation.vue'
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'
import MixinScrolling from '@/mixins/scrolling'
import cookies from 'vue-cookies'
import gaAPI from '@/utils/ga/api'
import loggerAPI from '@/services/api/logger'

export default {
	name: 'Search',
	mixins: [MixinScrolling],
	components: {
		ProductList,
		SearchItemTitle,
		FilterBox,
		BrandTabs,
		HeaderSearchBox,
		Loading,
		NoProductList,
		CommonPagenation,
	},
	computed: {
		...mapState('catalog', ['catalogList']),
		...mapState('seller', ['seller']),
		...mapState('brand', ['brand']),
		...mapState('delivery', ['basicAddress']),
		...mapState('user', ['user']),
		...mapGetters('user', ['instituteId']),
		...mapGetters('catalog', ['getSellerFilterList', 'getBrandFilterList']),
		...mapGetters('category', ['getParentCategoryList']),
		info() {
			if (this.$route.path.indexOf('brand') >= 0) {
				return JSON.parse(JSON.stringify(this.brand))
			} else {
				return JSON.parse(JSON.stringify(this.seller))
			}
		},
		sortTabList() {
			let list = []

			list.push({ id: 1, value: 'FAVORITE', direction: 'DESC', name: '구매 많은순' })
			list.push({ id: 2, value: 'PRICE', direction: 'ASC', name: '낮은 가격순' })
			list.push({ id: 3, value: 'PRICE', direction: 'DESC', name: '높은 가격순' })

			return list
		},
		placeholder() {
			if (this.mode == 'brand') {
				return '이 브랜드의 상품 검색'
			} else if (this.mode == 'seller') {
				return '이 판매자의 상품 검색'
			}
		},
		filterList() {
			let filters = {}
			// 카테고리는 화학물질 하나만 보여준다. (시약 외 상품은 별도 페이지)
			// 화학물질 목록, 브랜드별, 판매자별 검색 화면 모두 동일하다.
			const chemical = this.getParentCategoryList.filter(v => v.value === 'CAT_CHEMICAL')
			filters.filterCategoryList = chemical.length === 1 ? chemical : this.getParentCategoryList
			filters.filterBrandList = this.getBrandFilterList
			filters.filterSellerList = this.getSellerFilterList
			return filters
		},
		totalCount() {
			return Number(this.catalogList?.count) || 0
		},
		// 엔진이 앞쪽 MAX_RESULT건까지만 이동할 수 있어서 페이저는 그 범위까지만 만든다.
		pagerTotal() {
			return Math.min(this.totalCount, this.MAX_RESULT)
		},
		isOverLimit() {
			return this.totalCount > this.MAX_RESULT
		},
		isNoData() {
			return !this.isLoading && this.list.length === 0
		},
		getLoadingKeyword() {
			let result = ''
			if (this.$route.params.category === 'brand' || this.$route.params.category === 'seller')
				result += `"${this.$route.params.category === 'brand' ? this.brand.name : this.seller.name}"으로 검색 중`
			else if (this.keyword && this.keyword !== '') result += `"${this.keyword}"으로 검색 중 `
			else result += '카테고리 검색 중'

			return result
		},
		currentAddressId() {
			return cookies.get('currentAddress2')?.id ?? this.basicAddress?.id
		},
		currentInstituteId() {
			return cookies.get('currentInstitute2')?.id ?? this.instituteId
		},
		showFilter() {
			if (this.$route.query.keyword) {
				if (this.list.length > 0) {
					return true
				} else {
					if (this.tags) {
						return true
					} else {
						return false
					}
				}
			} else {
				return true
			}
		},
	},
	watch: {
		'$route.params.category': {
			deep: true,
			handler(val) {
				this.$router.go()
			},
		},
		'$route.query.category': {
			deep: true,
			handler(val) {
				this.$router.go()
			},
		},
		'$route.query.keyword': {
			deep: true,
			handler(val) {
				this.$router.go()
			},
		},
		async selectTab() {
			this.offset = 0
			this.pagerKey += 1
			await this.setList()
		},
		async selectedFilter(filter) {
			this.offset = 0
			this.pagerKey += 1
			if (this.$route.path.indexOf('brand') >= 0) this.sellerIds = []
			else if (this.$route.path.indexOf('seller') >= 0) this.brandIds = []
			else {
				this.sellerIds = []
				this.brandIds = []
			}
			this.tags = []
			this.categoryCode = ''
			this.categoryKorName = ''
			filter.forEach(item => {
				if (item.type === 'seller') this.sellerIds.push(item.value)
				else if (item.type === 'brand') this.brandIds.push(item.value)
				else if (item.type === 'category') {
					this.categoryCode = item.value
					this.categoryKorName = item.name
				} else if (item.type === 'tag') this.tags.push(item.value)
			})
			await this.setList()
		},
	},
	data() {
		return {
			mode: null,
			classification: null,
			category: null,
			selectedFilter: [],
			isLoading: true,
			list: [],
			selectTab: 'FAVORITE',
			keyword: '',
			keywordType: 'TEXT',
			keywordTarget: 'ALL',
			offset: 0,
			length: 20,
			pagerKey: 0,
			MAX_RESULT: 10000, // 엔진 index.max_result_window (offset + length 상한)
			order: [],
			orderDirection: ['DESC'],
			categoryCode: '',
			sellerIds: [],
			brandIds: [],
			tags: [],
			categoryKorName: '',
		}
	},
	created() {
		this.mode = this.$route.params.category ? this.$route.params.category : 'list'
	},
	mounted() {
		this.tags = this.$route.query.tags ? [this.$route.query.tags] : []
		this.classification = this.$route.query.classification
		this.category = this.$route.query.category
		if (this.$route.query.keyword) {
			this.keyword = this.$route.query.keyword
			this.keywordTarget = this.$route.query.type
		}
		this.loadList()
	},
	beforeDestroy() {
		this.clearFilter()
	},
	methods: {
		...mapActions('catalog', ['lookupCatalogList']),
		...mapActions('seller', ['lookupSeller']),
		...mapActions('brand', ['lookupBrand']),
		...mapActions('category', ['lookupParentCategoryList']),
		...mapMutations('catalog', ['clearFilter']),
		async loadList() {
			await this.lookupParentCategoryList()
			this.order = [this.selectTab]
			await this.setInfo()
			await this.setList()
		},
		async setList() {
			if (this.category && this.classification && (this.categoryCode != this.category || !this.categoryCode)) return
			else if (!this.category && this.classification && (this.categoryCode != this.classification || !this.categoryCode)) return
			this.classification = null
			this.category = null
			const params = {
				keyword: this.keyword,
				keywordType: this.keywordType,
				keywordTarget: this.keywordTarget,
				offset: this.offset,
				length: this.length,
				order: this.order,
				orderDirection: [this.orderDirection],
				sellerIds: this.sellerIds,
				brandIds: this.brandIds,
				// 브랜드 페이지의 화학물질 탭은 화학물질만 (용품은 실험용품/기기 탭)
				categoryKorName: this.categoryKorName || (this.mode === 'brand' ? '화학물질' : ''),
				tags: this.tags,
			}

			if (this.currentAddressId) params.deliveryId = this.currentAddressId
			if (this.currentInstituteId) params.instituteId = this.currentInstituteId

			this.isLoading = true
			await this.lookupCatalogList(params)
			this.list = JSON.parse(JSON.stringify(this.catalogList.list))
			this.isLoading = false
			gaAPI.gaEventSearch({
				keyword: params.keyword,
				type: params.keywordType,
				category_id: params.categoryCode,
			})
		},
		async setInfo() {
			if (this.$route.path.indexOf('brand') >= 0) {
				await this.lookupBrand(this.$route.query.id)
				this.brandIds = [this.$route.query.id]
			} else if (this.$route.path.indexOf('seller') >= 0) {
				await this.lookupSeller(this.$route.query.id)
				this.sellerIds = [this.$route.query.id]
			}
		},
		formatCount(num) {
			return Number(num || 0).toLocaleString('ko-KR')
		},
		// 페이저에서 페이지를 눌렀을 때
		async onPage({ offset }) {
			if (offset === this.offset) return
			this.offset = offset
			await this.setList()
			window.scrollTo({ top: 0, behavior: 'smooth' })
		},
		sortProduct(tab) {
			this.orderDirection = tab.direction
			this.selectTab = tab
			this.order = [this.selectTab.value]
		},
		async searchSuccess({ itemType, itemValue }) {
			await loggerAPI.searchSuccess({
				user_id: this.user.id,
				session_id: '',
				keyword: this.keyword,
				type: this.keywordType,
				itemType,
				itemValue,
			})
		},
		search(value) {
			this.keyword = value
			this.offset = 0
			this.pagerKey += 1
			this.setList()
		},
		setSelectedFilter(list) {
			this.selectedFilter = list
		},
		selectedCategory() {
			let selectedCategory = {}
			this.getParentCategoryList.forEach(item => {
				if (item.value == this.classification) {
					selectedCategory = item
				}
			})
			if (this.category) {
				selectedCategory.category = this.category
			}
			return selectedCategory
		},
	},
}
</script>

<style lang="scss" scoped>
#Search {
	/* 상단: 실험용품/기기 페이지와 같은 파란 배너 */
	.search-panel.hero {
		margin: 24px 0 4px;
	}
	.chemical-search {
		margin: 0 0 20px;
		padding: 32px 40px;
		border-radius: 12px;
		background: linear-gradient(120deg, #1d4f80 0%, #3d86c6 100%);
		color: #fff;
		.banner-label {
			font-size: 0.75rem;
			font-weight: 700;
			letter-spacing: 0.12em;
			opacity: 0.8;
		}
		.banner-head {
			display: flex;
			align-items: baseline;
			flex-wrap: wrap;
			gap: 4px 16px;
			margin: 6px 0 20px;
		}
		h2 {
			margin: 0;
			font-size: 1.6rem;
			font-weight: 700;
		}
		p {
			margin: 0;
			opacity: 0.9;
		}
		/* 검색창: 흰 알약 + 은은한 그림자, 포커스 시 노란 링 */
		::v-deep .search-box-wrapper {
			width: 100%;
			max-width: 640px;
			height: 52px;
			margin: 0;
			border: 1px solid #e5e7eb;
			border-radius: 24px;
			background: #fff;
			box-shadow: none;
			&:focus-within {
				border-color: #fabf14;
				box-shadow: 0 0 0 3px rgba(250, 191, 20, 0.28);
			}
			.dropdown {
				width: auto;
				margin-left: 8px;
				border-radius: 20px;
				/* 드롭다운 버튼이 박스 높이 전체를 써서 글씨가 세로 가운데에 오도록 */
				.dropdown-toggle {
					display: flex;
					align-items: center;
					height: 100% !important;
						border-radius: 20px;
				}
				.type {
					height: 34px;
						padding: 0 6px 0 14px;
						border-radius: 20px;
						background: #eef4fa;
						color: #1d4f80;
						font-size: 14px;
						font-weight: 700;
				}
				/* 오른쪽 노란 검색 버튼과 겹치는 왼쪽 돋보기 아이콘 제거 */
				.type::before {
					display: none;
				}
			}
			input {
				flex: 1;
				width: auto;
				min-width: 0;
				padding-left: 20px;
				font-size: 16px;
			}
			.icon-wrapper .search {
				width: 38px;
				height: 38px;
				margin-right: 6px;
			}
			.keyword-list {
				width: 100%;
				text-align: left;
			}
		}
	}
	.result-summary {
		display: flex;
		align-items: baseline;
		flex-wrap: wrap;
		gap: 4px 12px;
		margin-top: 20px;
		font-size: 0.9rem;
		color: #4a5764;
		b {
			font-size: 1.05rem;
			color: #111827;
		}
		.limit-note {
			font-size: 0.8rem;
			color: #98a2ad;
		}
	}
	.search-pager {
		margin: 32px 0 40px;
	}
}

/* 모바일: 상단 배너와 검색창을 작게 */
html.is-mobile #Search {
	.chemical-search {
		padding: 20px 18px;
		.banner-head {
			margin-bottom: 14px;
			p {
				font-size: 0.82rem;
			}
		}
		h2 {
			font-size: 1.35rem;
		}
		::v-deep .search-box-wrapper {
			max-width: none;
			height: 46px;
			.dropdown {
				margin-left: 6px;
			}
			input {
				padding-left: 12px;
				font-size: 14px;
			}
		}
	}
	.result-summary {
		margin-top: 14px;
	}
}
</style>
